import type { FC } from 'react'
import React, { useCallback, memo, useState, useEffect } from 'react'
import { cloneDeep } from 'lodash'
import { ResponsiveBar } from '@nivo/bar'
import ChartLegend from '../ChartLegend'
import ChartTooltip from '../ChartTooltip'
import { isEqual } from '@homeviews/utils'
import { CHART_COLORS } from '../../helpers/settings'
import NoData from '../NoData'
import CustomBar from '../CustomBar'
import { chartTheme } from '../../helpers/layout'

interface IProps {
  data: any
  gridRef?: HTMLElement
  reviewBenchmarkData: any
  dataMap: any
  labelsMap: any
  hasBenchmark?: boolean
  chartType?: 'default' | 'ageRange'
  developmentType?: any
}

const BenchmarkLayer =
  ({ benchmarkData, disabledDatasets }) =>
  (layerProps) => {
    if (benchmarkData && layerProps?.bars?.length) {
      return layerProps.bars
        .map((bar) => {
          // requires some transformations on data sets to extract proper values. see benchmarkData and layerProps variables
          try {
            const attributes = bar.key.split('.')

            const accessor = `${attributes[1]}`

            if (
              disabledDatasets &&
              disabledDatasets.find(
                (filterValue) => filterValue === attributes[0],
              )
            ) {
              return null
            }

            if (!bar.data?.value) {
              return null
            }

            return (
              <g key={bar.key}>
                <line
                  x1={bar.x + bar.width / 2}
                  x2={bar.x + bar.width / 2}
                  y1={
                    layerProps.yScale(
                      benchmarkData.data[attributes[0]][accessor],
                    ) - 1.25
                  }
                  y2={
                    layerProps.yScale(
                      benchmarkData.data[attributes[0]][accessor],
                    ) + 1.25
                  }
                  fill="none"
                  stroke={CHART_COLORS.benchmarkColor}
                  strokeWidth="10"
                />
              </g>
            )
          } catch {
            return null
          }
        })
        .filter(Boolean)
    } else {
      return null
    }
  }

const RatingsByChart: FC<IProps> = memo(
  ({
    data,
    gridRef,
    reviewBenchmarkData,
    dataMap,
    labelsMap,
    hasBenchmark,
    chartType,
    developmentType,
  }) => {
    const [disabledDatasets, setDisableDatasets] = useState<string[]>([])
    const [filteredData, setFilteredData] = useState<any>(data)
    const [showBenchmark, setShowBenchmark] = useState<any>(true)

    useEffect(() => {
      if (!filteredData) {
        return
      }

      const newFilteredData = cloneDeep(data)

      newFilteredData.forEach((item: any) => {
        disabledDatasets.map((filterValue: string) => {
          item[filterValue] = undefined
        })
      })

      if (
        disabledDatasets.find(
          (filterValue: string) => filterValue === 'benchmark',
        )
      ) {
        setShowBenchmark(false)
      } else {
        setShowBenchmark(true)
      }

      setFilteredData(newFilteredData)
    }, [disabledDatasets, data])

    const handleChartLegendClick = (id: string) => {
      let newDatasets = [...disabledDatasets]

      if (newDatasets.find((itemId: string) => itemId === id)) {
        newDatasets = newDatasets.filter((itemId: string) => itemId !== id)
      } else {
        newDatasets.push(id)
      }
      setDisableDatasets(newDatasets)
    }

    const getSettings = useCallback(() => {
      const chartSettings = {
        legendData: [],
        colors: [],
      }

      if (!data || !data?.length) {
        return chartSettings
      }

      Object.keys(dataMap).map((id: string) => {
        if (filteredData.find((item: any) => !!item[id])) {
          chartSettings.colors.push(
            hasBenchmark ? dataMap[id].benchmarkColor : dataMap[id].color,
          )
        }
        if (data.find((item: any) => !!item[id])) {
          chartSettings.legendData.push({
            id,
            text: dataMap[id].label,
            shapeColor: hasBenchmark
              ? dataMap[id].benchmarkColor
              : dataMap[id].color,
          })
        }
      })

      if (reviewBenchmarkData) {
        chartSettings.legendData.push({
          id: 'benchmark',
          text: `Benchmark Rating ${
            developmentType ? ` (${developmentType})` : ''
          }`,
          shapeColor: CHART_COLORS.benchmarkColor,
        })
      }

      return chartSettings
    }, [data, filteredData, reviewBenchmarkData])

    const { legendData, colors } = getSettings()

    const renderTooltip = (tooltip) => {
      const tooltipData = []
      legendData.forEach(({ id, text, textColor, shapeColor }) => {
        if (tooltip?.id !== id) {
          return
        }

        let formattedText = `${text}: ${tooltip?.data?.[id] || ''}`

        if (chartType === 'ageRange') {
          formattedText = `Age Range ${text}: ${tooltip?.data?.[id] || ''}`
        }

        tooltipData.push({
          text: formattedText,
          textColor,
          shapeColor,
        })
      })

      if (reviewBenchmarkData) {
        const singleBenchmarkValue =
          reviewBenchmarkData?.data?.[tooltip?.id]?.[tooltip?.indexValue]
        if (singleBenchmarkValue) {
          const formattedText = `Benchmark: ${singleBenchmarkValue || ''} ${
            developmentType ? ` (${developmentType})` : ''
          }`

          tooltipData.push({
            text: formattedText,
            shapeColor: CHART_COLORS.benchmarkColor,
          })
        }
      }
      return tooltipData
    }

    if (!data || !data?.length) {
      if (gridRef) {
        gridRef.style.display = 'none'
      }
      return <NoData />
    } else {
      if (gridRef) {
        gridRef.style.display = 'block'
      }
    }

    let keys = Object.keys(dataMap)

    try {
      keys = Object.keys(dataMap).filter((itemKey: any) => {
        return !!filteredData[0]?.[itemKey]
      })
    } catch {
      keys = Object.keys(dataMap)
    }

    return (
      <div className="l-chartContainer">
        <div className="l-chartWrapper">
          <ResponsiveBar
            data={filteredData}
            barComponent={(barProps) => (
              <CustomBar {...barProps} barLayout="ratingsTypeChart" />
            )}
            theme={chartTheme}
            animate={false}
            keys={keys}
            indexBy="id"
            borderRadius={2}
            colors={colors}
            isInteractive={true}
            enableGridX={false}
            enableLabel={false}
            groupMode="grouped"
            padding={0.3}
            // innerPadding={8}
            axisTop={null}
            axisRight={null}
            maxValue={5}
            axisBottom={{
              tickSize: 0,
              tickPadding: 10,
              tickRotation: 0,
              format: (value) => {
                return labelsMap?.[value]?.label || ''
              },
            }}
            axisLeft={{
              tickSize: 0,
              tickPadding: 10,
              tickRotation: 0,
              tickValues: [0, 1, 2, 3, 4, 5],
            }}
            margin={{ top: 50, right: 20, bottom: 50, left: 30 }}
            layers={[
              'grid',
              'axes',
              'bars',
              'markers',
              'legends',
              'annotations',
              BenchmarkLayer({
                benchmarkData: showBenchmark ? reviewBenchmarkData : null,
                disabledDatasets,
              }),
            ]}
            tooltip={(tooltip) => {
              return (
                <ChartTooltip>
                  <ChartLegend
                    direction="vertical"
                    data={renderTooltip(tooltip)}
                  />
                </ChartTooltip>
              )
            }}
          />
        </div>
        <ChartLegend
          data={legendData.map((item) => ({
            id: item.id,
            text: item.text,
            shapeColor: item.shapeColor,
            disabled: !!disabledDatasets.find(
              (datasetItem: any) => datasetItem === item.id,
            ),
          }))}
          disabledDatasets={disabledDatasets}
          onClick={handleChartLegendClick}
        />
      </div>
    )
  },
  (prevProps, nextProps) => isEqual(prevProps, nextProps),
)

export default RatingsByChart
